import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, Row, Spinner, Table } from 'react-bootstrap'
import {
  fetchDetailedHealth,
  runAiTest,
  sendTestEmail,
  type ExternalApiCheckResult,
} from '../services/api'
import { useAuth } from '../context/AuthContext'

export default function HealthChecks() {
  const { user } = useAuth()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['detailed-health'],
    queryFn: fetchDetailedHealth,
    refetchInterval: 30000,
  })

  const [testEmailTo, setTestEmailTo] = useState(user?.email || '')

  const emailTest = useMutation({
    mutationFn: () => sendTestEmail(testEmailTo.trim() || undefined),
  })
  const aiTest = useMutation({
    mutationFn: () => runAiTest(),
  })

  const emailError = (emailTest.error as any)?.response?.data?.error || (emailTest.error as any)?.message
  const aiErrorData = (aiTest.error as any)?.response?.data as ExternalApiCheckResult | undefined
  const aiError = aiErrorData?.error || aiErrorData?.detail || (aiTest.error as any)?.message

  return (
    <Container fluid>
      <div className="mb-4">
        <h1 className="mb-1">Health Checks</h1>
        <p className="text-muted mb-0">Detailed readiness checks for the API, worker, storage, email, Firecrawl, and Azure OpenAI.</p>
      </div>

      {isLoading && (
        <div className="text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Running health checks...
        </div>
      )}
      {isError && <Alert variant="danger">Failed to run detailed health checks.</Alert>}

      {data && (
        <>
          <Alert variant={data.status === 'Healthy' ? 'success' : 'danger'}>
            <strong>Overall status:</strong> {data.status}. Last checked {new Date(data.generatedUtc).toLocaleString()}.
          </Alert>

          <Row className="g-3 mb-4">
            <Col lg={8}>
              <Card>
                <Card.Header>Detailed API checks</Card.Header>
                <Card.Body>
                  <HealthCheckTable checks={data.checks} />
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="mb-3">
                <Card.Header>AI provider</Card.Header>
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Badge bg={data.aiProvider.isConfigured ? 'success' : 'danger'}>
                      {data.aiProvider.isConfigured ? 'Configured' : 'Not configured'}
                    </Badge>
                    <strong>{data.aiProvider.provider}</strong>
                  </div>
                  <dl className="mb-3 small">
                    <dt>Endpoint</dt>
                    <dd>{data.aiProvider.endpoint || '-'}</dd>
                    <dt>Deployment</dt>
                    <dd>{data.aiProvider.deploymentName || '-'}</dd>
                    <dt>API version</dt>
                    <dd>{data.aiProvider.apiVersion}</dd>
                    {data.aiProvider.configurationError && (
                      <>
                        <dt className="text-danger">Configuration issue</dt>
                        <dd className="text-danger">{data.aiProvider.configurationError}</dd>
                      </>
                    )}
                  </dl>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={aiTest.isPending || !data.aiProvider.isConfigured}
                    onClick={() => aiTest.mutate()}
                  >
                    {aiTest.isPending ? 'Calling model…' : 'Test AI (live model call)'}
                  </Button>
                  {aiTest.isSuccess && (
                    <Alert variant="success" className="mt-2 mb-0 py-2 small">
                      AI responded in {aiTest.data.durationMs} ms. {aiTest.data.detail}
                    </Alert>
                  )}
                  {aiTest.isError && (
                    <Alert variant="danger" className="mt-2 mb-0 py-2 small">
                      AI test failed: {aiError || 'Unknown error'}
                    </Alert>
                  )}
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>Email (SMTP)</Card.Header>
                <Card.Body>
                  <p className="small text-muted mb-2">
                    Sends a real test email through the configured SMTP server — the same path used by scheduled spot-check reports and Term Watch.
                  </p>
                  <InputGroup size="sm" className="mb-2">
                    <Form.Control
                      type="email"
                      placeholder="Recipient email"
                      value={testEmailTo}
                      onChange={(e) => setTestEmailTo(e.target.value)}
                    />
                    <Button
                      variant="outline-primary"
                      disabled={emailTest.isPending || !testEmailTo.trim()}
                      onClick={() => emailTest.mutate()}
                    >
                      {emailTest.isPending ? 'Sending…' : 'Test email'}
                    </Button>
                  </InputGroup>
                  {emailTest.isSuccess && (
                    <Alert variant="success" className="mb-0 py-2 small">
                      Test email sent to {emailTest.data.to} in {emailTest.data.durationMs} ms. Check the inbox (and spam folder).
                    </Alert>
                  )}
                  {emailTest.isError && (
                    <Alert variant="danger" className="mb-0 py-2 small">
                      Test email failed: {emailError || 'Unknown error'}
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}

function HealthCheckTable({ checks }: { checks: ExternalApiCheckResult[] }) {
  return (
    <Table responsive hover className="mb-0">
      <thead>
        <tr>
          <th>Check</th>
          <th>Status</th>
          <th>Duration</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {checks.map((check) => (
          <tr key={check.name}>
            <td className="fw-semibold">{check.name}</td>
            <td>
              <Badge bg={check.status === 'Healthy' ? 'success' : 'danger'}>{check.status}</Badge>
            </td>
            <td>{check.durationMs} ms</td>
            <td>
              <div>{check.detail || check.error || '-'}</div>
              {check.error && check.detail && <div className="small text-danger">{check.error}</div>}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}
